import React, { useEffect, useRef, useState } from 'react';
import { MessageSquarePlus, Paperclip, Search, X } from 'lucide-react';

import {
  loadMobileAccount, loadMobileAccounts, prepareFeedbackMedia, putFeedbackMedia, sendFeedback,
} from './api';
import { useXomicsAuth } from './AuthContext';
import { useDebounced } from './MobileAccounts';
import './feedback.css';
import { tx } from './i18n';

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime']);
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

// Keys, not words: these are read once when the module loads, before the
// catalogue has arrived, so they are translated where they are drawn.
const ABOUT = [
  { value: 'mobile_app', label: 'ui.feedback.about_mobile_app' },
  { value: 'xomics_portal', label: 'ui.feedback.about_xomics_portal' },
  { value: 'clinic_portal', label: 'ui.feedback.about_clinic_portal' },
  { value: 'store_website', label: 'ui.feedback.about_store_website' },
  { value: 'other', label: 'ui.feedback.about_other' },
];

const KINDS = [
  { value: 'bug', label: 'ui.feedback.kind_bug' },
  { value: 'idea', label: 'ui.feedback.kind_idea' },
  { value: 'other', label: 'ui.feedback.kind_other' },
];

async function sha256File(file) {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function fileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function megabytes(bytes) {
  return Math.floor(bytes / (1024 * 1024));
}

// Whose app the report is about. Staff have no app account of their own to read,
// and the person reporting is often not the person affected, so it is searched
// for rather than guessed. Only an account that may read the app directory sees
// this; everyone else fills the fields in themselves.
function MobileAccountPicker({ chosen, onChoose, disabled }) {
  const [search, setSearch] = useState('');
  const [matches, setMatches] = useState([]);
  const [looking, setLooking] = useState(false);
  const term = useDebounced(search.trim(), 280);

  useEffect(() => {
    if (chosen || term.length < 2) { setMatches([]); return undefined; }
    let current = true;
    setLooking(true);
    loadMobileAccounts({ q: term, limit: 6, sort: 'recent' })
      .then((answer) => { if (current) setMatches(answer.accounts || []); })
      .catch(() => { if (current) setMatches([]); })
      .finally(() => { if (current) setLooking(false); });
    return () => { current = false; };
  }, [term, chosen]);

  if (chosen) {
    return (
      <div className="xo-feedback-chosen">
        <span>
          <strong>{chosen.name || chosen.email || tx('ui.feedback.account_no_name')}</strong>
          <small>{[chosen.email, chosen.platform, chosen.build].filter(Boolean).join(' · ')}</small>
        </span>
        <button type="button" className="xo-btn-2" disabled={disabled} onClick={() => onChoose(null)}>
          <X aria-hidden="true" />
          {tx('ui.feedback.account_clear')}
        </button>
      </div>
    );
  }

  return (
    <div className="xo-feedback-picker">
      <div className="xo-feedback-picker-input">
        <Search aria-hidden="true" />
        <input className="xo-field" type="search" value={search} disabled={disabled}
          placeholder={tx('ui.feedback.account_search_placeholder')}
          onChange={(event) => setSearch(event.target.value)} />
      </div>
      {term.length >= 2 && (
        <ul className="xo-feedback-matches">
          {looking && <li className="is-muted">{tx('ui.feedback.account_searching')}</li>}
          {!looking && matches.length === 0 && <li className="is-muted">{tx('ui.feedback.account_none')}</li>}
          {matches.map((account) => (
            <li key={account.uuid}>
              <button type="button" disabled={disabled} onClick={() => onChoose(account)}>
                <strong>{account.name || account.email || tx('ui.feedback.account_no_name')}</strong>
                <small>{[account.email, account.platform].filter(Boolean).join(' · ')}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function XomicsFeedback() {
  const { user, permissions } = useXomicsAuth();
  const [mobileAccount, setMobileAccount] = useState(null);
  const [about, setAbout] = useState('xomics_portal');
  const [kind, setKind] = useState('bug');
  const [area, setArea] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [appVersion, setAppVersion] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [systemVersion, setSystemVersion] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState('');
  const [stage, setStage] = useState('editing');
  const [failed, setFailed] = useState('');
  const picker = useRef(null);

  const onMobileApp = about === 'mobile_app';
  const busy = stage === 'uploading' || stage === 'sending';
  const mayPickAccount = permissions.includes('PLATFORM_MOBILE_READ');

  // A thumbnail of whatever was picked, so a wrong screenshot is caught here
  // rather than by whoever reads the report. The object url is released when
  // the choice changes, or the page would hold every file picked this session.
  useEffect(() => {
    if (file === null || !IMAGE_TYPES.has(file.type)) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The directory knows the app version the account is on; the handset and its
  // system are not recorded, so they stay for whoever is reporting to fill in.
  async function chooseAccount(account) {
    setMobileAccount(account);
    if (account === null) { setAppVersion(''); return; }
    try {
      const detail = await loadMobileAccount(account.uuid);
      const build = detail?.activity?.build;
      if (build && build !== 'earlier') setAppVersion(String(build));
      setMobileAccount({ ...account, build: build && build !== 'earlier' ? String(build) : null });
    } catch {
      // The report is worth more than the version it could not read.
    }
  }

  function chooseFile(chosen) {
    setFileError('');
    if (!chosen) { setFile(null); return; }
    const isImage = IMAGE_TYPES.has(chosen.type);
    const isVideo = VIDEO_TYPES.has(chosen.type);
    if (!isImage && !isVideo) {
      setFile(null);
      setFileError(tx('ui.feedback.media_wrong_type'));
      return;
    }
    const limit = isImage ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES;
    if (chosen.size > limit) {
      setFile(null);
      setFileError(tx('ui.feedback.media_too_large', { limit: megabytes(limit) }));
      return;
    }
    setFile(chosen);
  }

  function clearFile() {
    setFile(null);
    setFileError('');
    if (picker.current) picker.current.value = '';
  }

  async function attach() {
    const declaration = { contentType: file.type, byteSize: file.size, sha256: await sha256File(file) };
    const prepared = await prepareFeedbackMedia(declaration);
    if (prepared.putUrl) await putFeedbackMedia(prepared.putUrl, file, file.type);
    return declaration;
  }

  // The server's own message names the limit it refused on, so it is shown as
  // it came; only a failure with no message of its own falls back.
  function reasonFor(error, where) {
    if (error?.code === 'FEEDBACK_STORAGE_UNAVAILABLE') return tx('ui.feedback.failed_storage');
    if (error?.message && String(error.code || '').startsWith('FEEDBACK_')) return error.message;
    if (where === 'uploading') return tx('ui.feedback.failed_upload');
    return tx('ui.feedback.failed');
  }

  async function submit(event) {
    event.preventDefault();
    setFailed('');
    let where = 'sending';
    try {
      let media = null;
      if (file) {
        where = 'uploading';
        setStage('uploading');
        media = await attach();
      }
      where = 'sending';
      setStage('sending');
      await sendFeedback({
        kind,
        about,
        area: area.trim(),
        message: message.trim(),
        contactEmail: email.trim(),
        ...(onMobileApp && mobileAccount ? { mobileAccountUuid: mobileAccount.uuid } : {}),
        ...(onMobileApp ? {
          appVersion: appVersion.trim(),
          deviceModel: deviceModel.trim(),
          systemVersion: systemVersion.trim(),
        } : {}),
        ...(media ? { media } : {}),
      });
      setStage('sent');
    } catch (error) {
      setStage('editing');
      setFailed(reasonFor(error, where));
    }
  }

  function again() {
    setArea('');
    setMessage('');
    clearFile();
    setFailed('');
    setStage('editing');
  }

  if (stage === 'sent') {
    return (
      <section className="xo-feedback xo-feedback-done">
        <h1>{tx("ui.feedback.thank_you_6b50e31a")}</h1>
        <p>{tx("ui.feedback.we_have_your_feedback_if_we_need_to_ask_you_anything_w_f8ed8904")}</p>
        <button type="button" className="xo-btn" onClick={again}>{tx("ui.feedback.send_another_00e106ca")}</button>
      </section>
    );
  }

  return (
    <section className="xo-feedback">
      <header className="xo-feedback-head">
        <span className="xo-feedback-icon"><MessageSquarePlus aria-hidden="true" /></span>
        <div>
          <h1>{tx("ui.feedback.feedback_c8d7677e")}</h1>
          <p>{tx("ui.feedback.tell_us_what_is_broken_or_what_would_help_it_reaches_t_f6886a24")}</p>
        </div>
      </header>

      <form className="xo-feedback-form" onSubmit={submit}>
        {/* Two answers that are already correct most of the time, kept visually
            lighter than the two fields that actually need thought. */}
        <div className="xo-feedback-choices">
          <label className="xo-feedback-field">
            <span>{tx('ui.feedback.label_product')}</span>
            <select className="xo-field" value={about} disabled={busy} onChange={(event) => setAbout(event.target.value)}>
              {ABOUT.map((option) => <option key={option.value} value={option.value}>{tx(option.label)}</option>)}
            </select>
          </label>

          <label className="xo-feedback-field">
            <span>{tx('ui.feedback.label_kind')}</span>
            <select className="xo-field" value={kind} disabled={busy} onChange={(event) => setKind(event.target.value)}>
              {KINDS.map((option) => <option key={option.value} value={option.value}>{tx(option.label)}</option>)}
            </select>
          </label>
        </div>

        <label className="xo-feedback-field xo-feedback-wide">
          <span>{tx('ui.feedback.label_screen')}</span>
          <input className="xo-field" type="text" maxLength={120} required value={area} disabled={busy}
            placeholder={tx("ui.feedback.for_example_shipments_validation_or_signing_in_3455df04")}
            onChange={(event) => setArea(event.target.value)} />
        </label>

        <label className="xo-feedback-field xo-feedback-wide">
          <span>{tx("ui.feedback.what_happened_e9ef659a")}</span>
          <textarea className="xo-field" rows={4} maxLength={4000} required value={message} disabled={busy}
            placeholder={tx("ui.feedback.if_something_broke_what_you_were_doing_just_before_hel_bdefd53b")}
            onChange={(event) => setMessage(event.target.value)} />
        </label>

        <label className="xo-feedback-field xo-feedback-wide">
          <span>{tx('ui.feedback.label_reply_to')}</span>
          <input className="xo-field" type="email" maxLength={320} required value={email} disabled={busy}
            onChange={(event) => setEmail(event.target.value)} />
          <small className="xo-feedback-hint">{tx('ui.feedback.hint_reply_to')}</small>
        </label>

        {onMobileApp && mayPickAccount && (
          <div className="xo-feedback-field xo-feedback-wide">
            <span>{tx('ui.feedback.account_label')}</span>
            <MobileAccountPicker chosen={mobileAccount} onChoose={chooseAccount} disabled={busy} />
            <small className="xo-feedback-hint">{tx('ui.feedback.account_hint')}</small>
          </div>
        )}

        {/* All three are optional and only ever apply to the app, so they are
            folded away rather than sitting there looking like work to do. */}
        {onMobileApp && (
          <details className="xo-feedback-details xo-feedback-wide">
            <summary>
              {tx('ui.feedback.app_details')}
              <small>{tx('ui.feedback.app_details_hint')}</small>
            </summary>
            <div className="xo-feedback-trio">
              <label className="xo-feedback-field">
                <span>{tx("ui.feedback.app_version_0e25be38")}</span>
                <input className="xo-field" type="text" maxLength={40} value={appVersion} disabled={busy}
                  onChange={(event) => setAppVersion(event.target.value)} />
              </label>
              <label className="xo-feedback-field">
                <span>{tx("ui.feedback.phone_77064d52")}</span>
                <input className="xo-field" type="text" maxLength={120} value={deviceModel} disabled={busy}
                  onChange={(event) => setDeviceModel(event.target.value)} />
              </label>
              <label className="xo-feedback-field">
                <span>{tx("ui.feedback.system_version_02b4d6e1")}</span>
                <input className="xo-field" type="text" maxLength={60} value={systemVersion} disabled={busy}
                  onChange={(event) => setSystemVersion(event.target.value)} />
              </label>
            </div>
          </details>
        )}

        <div className="xo-feedback-field xo-feedback-wide">
          <span>{tx("ui.feedback.add_a_photo_or_video_0e94f281")}</span>
          <div className="xo-feedback-file">
            {file === null ? (
              <button type="button" className="xo-btn-2" disabled={busy} onClick={() => picker.current?.click()}>
                <Paperclip aria-hidden="true" />
                {tx("ui.feedback.choose_a_file_74b1d89d")}
              </button>
            ) : (
              <div className="xo-feedback-chosen-file">
                {preview
                  ? <img src={preview} alt={tx('ui.feedback.attachment_preview')} />
                  : <span className="xo-feedback-file-icon"><Paperclip aria-hidden="true" /></span>}
                <span>
                  <strong>{file.name}</strong>
                  <small>{fileSize(file.size)}</small>
                </span>
                <button type="button" className="xo-btn-2" disabled={busy} onClick={clearFile}>
                  <X aria-hidden="true" />
                  {tx("ui.feedback.remove_e963907d")}
                </button>
              </div>
            )}
            <input ref={picker} type="file" hidden accept="image/jpeg,image/png,image/webp,image/avif,video/mp4,video/quicktime"
              onChange={(event) => chooseFile(event.target.files?.[0] || null)} />
          </div>
          {fileError
            ? <small className="xo-feedback-error">{fileError}</small>
            : <small className="xo-feedback-hint">{tx("ui.feedback.optional_photos_up_to_20_mb_video_up_to_50_mb_3d71692c")}</small>}
        </div>

        {failed && <p className="xo-feedback-failed" role="alert">{failed}</p>}

        <div className="xo-feedback-actions">
          <button type="submit" className="xo-btn" disabled={busy}>
            {stage === 'uploading' ? tx("ui.feedback.uploading_the_file_744410d0") : stage === 'sending' ? tx("ui.feedback.sending_ceafde86") : tx("ui.feedback.send_feedback_c431a262")}
          </button>
        </div>
      </form>
    </section>
  );
}

export default XomicsFeedback;
